import Link from 'next/link';

import { logger } from '@/lib/logger';

import { NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { routes } from '@/utils/routes';

import { AvatarSkeleton, TextSkeleton } from '../../Skeleton';

type TokenShortcutSummaryProps = {
  loading?: boolean;
  chainId?: number;
  title?: string;
  summary?: string;
  showSummary?: boolean;
  subtitle?: string;
  tokenAddress?: string;
};

export function TokenProfileRelatedTokenShortcutSummary({
  chainId,
  title,
  summary,
  subtitle,
  loading = false,
  tokenAddress,
}: TokenShortcutSummaryProps) {
  const networkName = !!chainId && NETWORK_NAME_FOR_URLPATH_PER_CHAIN_ID[chainId]?.toLowerCase();

  if (!networkName && !loading) {
    logger.warn('No network name for chainId', chainId);
    return null;
  }

  if (!tokenAddress && !loading) {
    logger.warn('No token address for chainId', chainId);
    return null;
  }

  return (
    <Link
      href={{
        pathname: routes.TOKEN_PROFILE,
        query: {
          chainId: networkName || '',
          contractAddresses: [tokenAddress || '', ''],
        },
      }}
      className="bg-special-background-card shadow-10 grid relative w-fit items-start justify-start pl-2.5 pr-3 py-2 rounded-md border border-solid border-token-gray-300 min-w-[200px] [grid-template-columns:auto_1fr] [grid-template-rows:repeat(2,auto)] gap-y-1 mobile:gap-x-2 tablet:gap-x-2 no-underline data-[loading=false]:hover:border-token-gray-400 data-[loading=false]:hover:bg-token-gray-200 data-[loading=true]:pointer-events-none"
      data-loading={loading}
      tabIndex={loading ? -1 : undefined}
      aria-disabled={loading}
      onClick={() => {
        const app = document.getElementById('app');
        app?.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      {loading ? (
        <div className="w-8 h-8 self-start col-start-1 row-start-1 row-span-2">
          <AvatarSkeleton size={32} />
        </div>
      ) : (
        <TokenAvatar
          address={tokenAddress}
          chainId={chainId}
          size={32}
          className="col-span-1 row-start-1 row-span-2 mr-2"
        />
      )}

      <div className="col-start-2 row-start-1 flex items-center gap-2 flex-wrap">
        <div
          className={fontStyles({
            variant: 'BODY_MD',
            class: ['text-token-gray-1600', 'font-semibold'],
          })}
        >
          {loading ? (
            <TextSkeleton className="text-token-gray-400 flex items-center h-[22px]" width={62} />
          ) : (
            <span>{title}</span>
          )}
        </div>

        <div
          className={fontStyles({
            variant: 'BODY_SM',
            class: ['text-token-gray-800'],
          })}
        >
          {loading ? (
            <TextSkeleton className="text-token-gray-400 flex items-center h-[18px]" width={40} />
          ) : (
            <span>{subtitle}</span>
          )}
        </div>
      </div>

      {/* Token Summary - Row 2 (Always shown) */}
      <div
        className={fontStyles({
          variant: 'BODY_SM',
          class: [
            'text-token-gray-1000',
            'col-start-2',
            'row-start-2',
            'line-clamp-2',
            'text-wrap',
          ],
        })}
      >
        {loading ? (
          <TextSkeleton className="text-token-gray-400 flex items-center h-4.5" width={120} />
        ) : (
          <span>{summary}</span>
        )}
      </div>
    </Link>
  );
}
